"use client";

import { useEffect, useState } from "react";
import { useImageStore } from "@/stores/imageStore";
import { imageService, UpdateImageDto } from "@/services/imageService";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { CalendarDays, User as UserIcon } from "lucide-react";
import { CopyButton } from "@/components/ui/copy-button";

interface ModalEditImageProps {
  isOpen: boolean;
  onClose: () => void;
  imageId: string | null;
  onComplete: () => void;
}

export default function ModalEditImage({
  isOpen,
  onClose,
  imageId,
  onComplete,
}: ModalEditImageProps) {
  const { update, isUpdating } = useImageStore();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<UpdateImageDto>({});
  const [preview, setPreview] = useState<string>("");
  const [info, setInfo] = useState<{
    filename?: string;
    mimeType?: string;
    sizeKB?: number;
    width?: number;
    height?: number;
  }>({});
  const [publicUrl, setPublicUrl] = useState<string>("");
  const [uploadedBy, setUploadedBy] = useState<string>("");
  const [createdAt, setCreatedAt] = useState<string>("");

  const isValidUrl = (v?: string) => {
    try {
      if (!v) return false;
      new URL(v);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !imageId) return;
      setLoading(true);
      try {
        const res = await imageService.getById(imageId);
        const data: any =
          (res as any).data?.data?.data ?? (res as any)?.data?.data;
        console.log("data11: ", data);
        if (data) {
          setPreview(data.url);
          setPublicUrl(data.url);
          setForm({
            title: data.title || "",
            alt: data.alt || "",
            caption: data.caption || "",
            linkImage: data.linkImage || "",
            rel: data.rel,
            target: data.target,
          });
          setInfo({
            filename: data.filename,
            mimeType: data.mimeType,
            sizeKB: Math.round((Number(data.size) || 0) / 1024),
            width: data.width,
            height: data.height,
          });
          const uploader =
            typeof data.uploadedBy === "string"
              ? data.uploadedBy
              : data.uploadedBy?.name || data.uploadedBy?.username || "";
          setUploadedBy(uploader);
          try {
            const d = new Date(data.createdAt);
            const dd = String(d.getDate()).padStart(2, "0");
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const yyyy = d.getFullYear();
            const hh = String(d.getHours()).padStart(2, "0");
            const mi = String(d.getMinutes()).padStart(2, "0");
            setCreatedAt(`${hh}:${mi} ${dd}/${mm}/${yyyy}`);
          } catch {
            setCreatedAt("");
          }
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, imageId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageId) return;
    const ok = await update(imageId, form);
    if (ok) {
      onComplete();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-6xl p-0">
        <form onSubmit={submit}>
          <DialogHeader className="px-6 py-4 border-b border-gray-200">
            <DialogTitle>Chỉnh sửa ảnh</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="p-6 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {preview && (
                  <img
                    src={preview}
                    className="rounded-lg shadow-md max-h-80 w-auto object-contain mx-auto"
                  />
                )}
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Không cập nhật file ảnh tại đây, chỉ chỉnh metadata.
                </p>
                {/* Thông tin tệp tin */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 text-sm text-gray-700">
                  <div className="text-xs font-semibold text-blue-700 mb-2 uppercase tracking-wide">
                    THÔNG TIN TỆP TIN
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tên tệp:</span>
                      <span
                        className="font-medium text-gray-900 truncate ml-2 max-w-[220px]"
                        title={info.filename}
                      >
                        {info.filename}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loại:</span>
                      <span className="font-medium text-gray-900">
                        {info.mimeType}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dung lượng:</span>
                      <span className="font-medium text-gray-900">
                        {info.sizeKB} KB
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kích thước:</span>
                      <span className="font-medium text-gray-900">
                        {info.width ?? "-"} x {info.height ?? "-"} px
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    {createdAt && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 flex items-center gap-2">
                          <CalendarDays className="h-4 w-4" /> Tải lên vào:
                        </span>
                        <span className="font-medium text-gray-900">
                          {createdAt}
                        </span>
                      </div>
                    )}
                    {uploadedBy && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 flex items-center gap-2">
                          <UserIcon className="h-4 w-4" /> Đã tải lên bởi:
                        </span>
                        <span className="font-medium text-gray-900">
                          {uploadedBy}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {/* URL ảnh tự động */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    URL ảnh (tự động)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      value={publicUrl}
                      disabled
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <CopyButton value={publicUrl} />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Tiêu đề
                  </label>
                  <input
                    value={form.title || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, title: e.target.value }))
                    }
                    placeholder="Nhập tiêu đề cho ảnh..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Alt Text (SEO)
                  </label>
                  <input
                    value={form.alt || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, alt: e.target.value }))
                    }
                    placeholder="Mô tả thay thế cho ảnh..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Caption
                  </label>
                  <input
                    value={form.caption || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, caption: e.target.value }))
                    }
                    placeholder="Chú thích cho ảnh..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Link ảnh (tùy chọn)
                  </label>
                  <input
                    value={form.linkImage || ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, linkImage: e.target.value }))
                    }
                    placeholder="https://..."
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      form.linkImage && !isValidUrl(form.linkImage)
                        ? "border-red-500 focus:ring-red-500 bg-red-50"
                        : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  />
                </div>
                {(form.linkImage || "").trim() &&
                  isValidUrl(form.linkImage) && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Rel (thuộc tính liên kết)
                        </label>
                        <Select
                          value={form.rel || "nofollow"}
                          onValueChange={(v) =>
                            setForm((p) => ({ ...p, rel: v as any }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn Rel" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="nofollow">
                              Không theo dõi
                            </SelectItem>
                            <SelectItem value="noreferrer">
                              Ẩn nguồn giới thiệu
                            </SelectItem>
                            <SelectItem value="noopener">
                              Ngăn cửa sổ cha (noopener)
                            </SelectItem>
                            <SelectItem value="sponsored">
                              Liên kết tài trợ/quảng cáo
                            </SelectItem>
                            <SelectItem value="ugc">
                              Nội dung do người dùng tạo
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1.5">
                          Chỉ áp dụng khi ảnh có link.
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Target (cách mở liên kết)
                        </label>
                        <Select
                          value={form.target || "_self"}
                          onValueChange={(v) =>
                            setForm((p) => ({ ...p, target: v as any }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Chọn cách mở" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_self">
                              Trong tab hiện tại
                            </SelectItem>
                            <SelectItem value="_blank">Mở tab mới</SelectItem>
                            <SelectItem value="_parent">
                              Khung cha (_parent)
                            </SelectItem>
                            <SelectItem value="_top">
                              Cửa sổ trên cùng (_top)
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          <DialogFooter className="px-6 py-4 border-t border-gray-200 bg-gray-50 sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={isUpdating}
              className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium cursor-pointer disabled:opacity-50"
            >
              Đóng
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-all font-medium shadow-md hover:shadow-lg"
            >
              {isUpdating ? "Đang lưu..." : "Lưu thay đổi"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
